import React from 'react';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import {
    selectOwnWaitingRoomDeniedCause,
    selectOwnWaitingRoomStanding,
} from 'flow/selectors/video-selectors';
import {
    WaitingRoomStanding,
    getAdmissionDeniedMessageKey,
    getAdmissionDeniedTitleKey,
} from 'flow/utils/meeting-constants';

/**
 * Where someone standing at the door (VID-76) is told how it is going, laid over
 * their own preview rather than tucked into the page: they are looking at the
 * video the whole time they wait, so that is where both the wait and its answer
 * belong.
 *
 * The wait is a single line, because there is nothing to say yet. A refusal is a
 * headed message, because it is the meeting answering them.
 *
 * Renders nothing when there is no admission to report — which is everyone who
 * was never held back, and anyone already let in.
 */
const MeetingLobbyAdmissionNotice = () => {
    const { i18next } = useAppContext();
    const ownWaitingRoomStanding = useAppSelector(selectOwnWaitingRoomStanding);
    const cause = useAppSelector(selectOwnWaitingRoomDeniedCause);

    if (ownWaitingRoomStanding === WaitingRoomStanding.WAITING) {
        return (
            <div className="absolute inset-0 flex items-center justify-center p-6">
                <div
                    className="rounded-2xl bg-gray-800/95 px-5 py-3 text-center text-sm font-bold text-white"
                    data-test-id="meeting-waiting-for-approval"
                >
                    {i18next.t(
                        'videoconference.waiting_room.waiting_for_approval',
                    )}
                </div>
            </div>
        );
    }

    // VID-471: removed mid-meeting, not turned away at the door — same slot, no title.
    if (ownWaitingRoomStanding === WaitingRoomStanding.REMOVED) {
        return (
            <div
                className="absolute inset-0 flex items-center justify-center p-6"
                data-test-id="meeting-removed-by-host"
            >
                <div className="max-w-xs rounded-2xl bg-gray-800/95 px-6 py-5 text-center text-sm text-white">
                    {i18next.t('videoconference.host_controls.removed_by', {
                        by: cause,
                    })}
                </div>
            </div>
        );
    }

    if (ownWaitingRoomStanding !== WaitingRoomStanding.DENIED) {
        return null;
    }

    const titleKey = getAdmissionDeniedTitleKey(cause);

    return (
        <div
            className="absolute inset-0 flex items-center justify-center p-6"
            data-test-id="meeting-admission-denied"
        >
            <div className="max-w-xs rounded-2xl bg-gray-800/95 px-6 py-5 text-center text-white">
                {titleKey ? (
                    <div className="text-base font-bold">
                        {i18next.t(titleKey)}
                    </div>
                ) : null}
                <div className="mt-1 text-sm">
                    {i18next.t(getAdmissionDeniedMessageKey(cause))}
                </div>
            </div>
        </div>
    );
};

export default MeetingLobbyAdmissionNotice;
